import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { supabase } from '../../../lib/supabase';

export interface FlexbandInitialLessonRow {
  school_id: string;
  lesson_id: string;
  course_id: string;
  lesson_date: string;
  start_datetime: string;
  end_datetime: string;
  lesson_type: string | null;
  is_cancelled: boolean;
  period_label: string | null;
  period_number: number | null;
  room_id: string | null;
  room_name: string | null;
  room_number: string | null;
  subject_id: string | null;
  subject_name: string | null;
  course_name: string | null;
  student_count: number | null;
  student_names_with_class: string[] | null;
  student_ids: string[] | null;  // NEW: Added for UUID matching
  teacher_ids: string[] | null;
  teacher_names: string[] | null;
  teacher_kurzels: string[] | null;
  primary_teacher_id: string | null;
  original_teacher_kurzel: string | null;
  primary_teacher_name: string | null;
  is_substitute: boolean | null;
  substitute_id: string | null;
  substitute_type: string | null;
  substitute_status: string | null;
  substitute_primary_teacher_id: string | null;
  substitute_primary_teacher_name: string | null;
  substitute_begleitung_teacher_ids: string[] | null;
  substitute_begleitung_teacher_names: string[] | null;
  substitute_begleitung_teacher_kurzels: string[] | null;
  substitute_room_id: string | null;
  substitute_room_name: string | null;
  substitute_room_number: string | null;
  substitute_absent_teacher_ids: string[] | null;
  substitute_is_cancelled: boolean | null;
  substitute_cancel_reason: string | null;
  lesson_cancelled: boolean | null;
  substitution_cancelled: boolean | null;
  derived_cancelled: boolean | null;
  is_betreuung: boolean | null;
  total_enrolled: number | null;
  total_guests: number | null;
  present_count: number | null;
  late_count: number | null;
  absent_excused_count: number | null;
  absent_unexcused_count: number | null;
  /** New post-absent_unclear-epic: count of pre-klärung absences.
   *  Optional/nullable while consumers transition. */
  absent_unclear_count: number | null;
  attendance_done: boolean | null;
  substitute_notes: string | null;  // NEW: Notes attached to substitution (shown on cancelled lessons)
  floor: string | null;  // Floor from structure_rooms, folded into initial state RPC
}

export interface FlexbandInitialStateArgs {
  schoolId: string | null;
  date: string; // ISO date (YYYY-MM-DD)
}

export interface FlexbandSidebarAbsenceRow {
  student_id: string;
  presence_status: string;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  class_id: string | null;
  class_name?: string | null;
}

export interface FlexbandSidebarState {
  issues: any[];
  absences: FlexbandSidebarAbsenceRow[];
  unclear_absences: any[];
  school_days: number[];  // day_id values (Sun=1, Mon=2, ... Sat=7)
}

export interface FlexbandSidebarStateArgs {
  date: string; // ISO date (YYYY-MM-DD)
  /**
   * When true, the RPC also returns "Heute zu prüfen" entries for
   * students who have not yet checked in today (no daily-log row, or
   * daily-log row with check_in_time IS NULL). Defaults to false to
   * preserve the original "checked-in only" behaviour.
   */
  includeNotCheckedIn?: boolean;
}



export const flexbandApi = createApi({
  reducerPath: 'flexbandApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: ['FlexbandInitial', 'FlexbandSidebar'],
  endpoints: (builder) => ({
    getInitialState: builder.query<FlexbandInitialLessonRow[], FlexbandInitialStateArgs>({
      async queryFn(args) {
        try {
          const { schoolId, date } = args;
          if (!schoolId) {
            return { data: [] };
          }
          const { data, error } = await (supabase as any).rpc('fn_flexband_get_initial_state', {
            p_school_id: schoolId,
            p_date: date,
          });
          if (error) {
            return { error } as any;
          }
          return { data: (data || []) as FlexbandInitialLessonRow[] };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (_result, _error, args) => [{ type: 'FlexbandInitial', id: `${args.schoolId ?? 'none'}:${args.date}` }],
    }),
    getSidebarState: builder.query<FlexbandSidebarState | null, FlexbandSidebarStateArgs>({
      async queryFn(args) {
        try {
          const { date, includeNotCheckedIn } = args;
          const { data, error } = await (supabase as any).rpc('fn_flexband_get_sidebar_state', {
            p_date: date,
            p_include_not_checked_in: !!includeNotCheckedIn,
          });
          if (error) {
            return { error } as any;
          }
          const payload = (data || null) as FlexbandSidebarState | null;
          return { data: payload };
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (_result, _error, args) => [
        {
          type: 'FlexbandSidebar',
          id: `${args.date}:${args.includeNotCheckedIn ? 'all' : 'checkedin'}`,
        },
      ],
    }),
  }),
});

export const { useGetInitialStateQuery, useGetSidebarStateQuery } = flexbandApi;
